import * as THREE from 'three';

export class SimplePhysics {
    constructor() {
        this.isInitialized = true; // Always initialized (no async loading)
        this.gravity = new THREE.Vector3(0, -9.81, 0);
        this.bodies = [];
        this.debugPhysicsLogging = false;
        this.colliders = [];
        this.usingFallbackPhysics = false; // Not relevant here, but kept for API consistency
        this.terrainHeightMap = null; // Set via setTerrainHeightMap()

        // For debug visualization
        this.setupDebugRenderer();
    }
    
    // Initialize physics (kept for API consistency)
    async init() {
        console.log("Simple physics system initialized");
        return true;
    }
    
    setupDebugRenderer() {
        // Create a lines object for debug visualization
        const material = new THREE.LineBasicMaterial({
            color: 0x00ff00,
            vertexColors: true
        });
        
        this.debugLines = new THREE.LineSegments(
            new THREE.BufferGeometry(),
            material
        );
        this.debugLines.visible = false;
        this.debugRenderer = this.debugLines;
    }
    
    update(deltaTime) {
        // Update each physics body
        for (const body of this.bodies) {
            if (!body.isDynamic) continue;
            
            // Apply gravity
            body.velocity.y += this.gravity.y * deltaTime;
            
            // Apply forces
            if (body.forces) {
                body.velocity.x += body.forces.x * deltaTime / body.mass;
                body.velocity.y += body.forces.y * deltaTime / body.mass;
                body.velocity.z += body.forces.z * deltaTime / body.mass;
                
                // Reset forces
                body.forces.set(0, 0, 0);
            }
            
            // Apply torques if present
            if (body.torque) {
                // Initialize angular velocity if not present
                if (!body.angularVelocity) {
                    body.angularVelocity = new THREE.Vector3(0, 0, 0);
                }
                
                // Convert torque to angular acceleration based on inertia
                const inertiaFactor = 1 / body.mass; // Simplified inertia
                body.angularVelocity.x += body.torque.x * deltaTime * inertiaFactor;
                body.angularVelocity.y += body.torque.y * deltaTime * inertiaFactor;
                body.angularVelocity.z += body.torque.z * deltaTime * inertiaFactor;
                
                // Apply angular damping
                if (body.angularDamping) {
                    const dampingFactor = Math.pow(1.0 - body.angularDamping, deltaTime);
                    body.angularVelocity.multiplyScalar(dampingFactor);
                }
                
                // Apply maximum angular velocity if specified
                if (body.maxAngularVelocity) {
                    const angVelLength = body.angularVelocity.length();
                    if (angVelLength > body.maxAngularVelocity) {
                        body.angularVelocity.multiplyScalar(body.maxAngularVelocity / angVelLength);
                    }
                }
                
                // Apply angular velocity to rotation
                const deltaQuat = new THREE.Quaternion().setFromEuler(
                    new THREE.Euler(
                        body.angularVelocity.x * deltaTime,
                        body.angularVelocity.y * deltaTime,
                        body.angularVelocity.z * deltaTime,
                        'XYZ'
                    )
                );
                body.quaternion.premultiply(deltaQuat);
                body.quaternion.normalize();
                
                // Reset torque
                body.torque.set(0, 0, 0);
            }
            
            // Apply linear damping
            if (body.linearDamping) {
                const dampingFactor = Math.pow(1.0 - body.linearDamping, deltaTime);
                body.velocity.multiplyScalar(dampingFactor);
            }
            
            // Update position based on velocity
            body.position.x += body.velocity.x * deltaTime;
            body.position.y += body.velocity.y * deltaTime;
            body.position.z += body.velocity.z * deltaTime;
            
            // Check for ground collision
            this.handleGroundCollision(body);
            
            // Skip trigger objects in collision tests.
            // Bodies with collisionFilterGroup=0 are simulation-only and opt out
            // of expensive broadphase collision checks.
            if (!body.isTrigger && body.collisionFilterGroup !== 0) {
                // Check for collisions with other bodies
                this.checkCollisions(body);
            }
            
            // Check for trigger overlaps
            if (this.triggers && this.triggers.length > 0 && body.type === 'box') {
                try {
                    const overlappingTriggers = this.checkTriggerOverlaps(body);
                    
                    // Handle trigger overlaps
                    for (const trigger of overlappingTriggers) {
                        // If the trigger has userData, it's likely a coin
                        if (trigger.userData && !trigger.userData.collected) {
                            // If the body has a bus reference, call collectCoin on it
                            if (body.bus && typeof body.bus.collectCoin === 'function') {
                                body.bus.collectCoin(trigger.userData);
                            } 
                            // Fallback to directly calling collect on the coin
                            else if (typeof trigger.userData.collect === 'function') {
                                trigger.userData.collect();
                            }
                        }
                    }
                } catch (error) {
                    console.error("Error in trigger overlap check:", error);
                }
            }
            
            // Update the Three.js mesh positions if any
            if (body.threeMesh) {
                body.threeMesh.position.copy(body.position);
                body.threeMesh.quaternion.copy(body.quaternion);
            }
        }
        
        // Update debug visualization
        if (this.debugRenderer && this.debugRenderer.visible) {
            this.updateDebugVisualization();
        }
    }
    
    setTerrainHeightMap(heightMap) {
        this.terrainHeightMap = heightMap;
    }

    handleGroundCollision(body) {
        // Get terrain height at body's XZ position (falls back to 0 for flat ground)
        const groundY = this.terrainHeightMap
            ? this.terrainHeightMap.getHeight(body.position.x, body.position.z)
            : 0;

        if (body.type === 'sphere') {
            const minY = groundY + body.radius + 0.01;
            if (body.position.y < minY) {
                body.position.y = minY;

                if (body.velocity.y < 0) {
                    body.velocity.y = -body.velocity.y * body.restitution;
                    body.velocity.x *= 0.9995;
                    body.velocity.z *= 0.9995;
                }
            }
        } else {
            const minY = groundY + (body.halfExtents ? body.halfExtents.y : 0) + 0.01;
            if (body.position.y < minY) {
                body.position.y = minY;

                if (body.velocity.y < 0) {
                    body.velocity.y = -body.velocity.y * body.restitution;
                    body.velocity.x *= 0.9995;
                    body.velocity.z *= 0.9995;
                }
            }
        }
    }
    
    checkCollisions(body) {
        try {
            for (const collider of this.colliders) {
                // Skip self-collision and trigger objects
                if (collider === body || collider.isTrigger) continue;
                
                // Make sure both objects have the necessary properties
                if (!collider.type || !body.type) continue;
                
                if (collider.type === 'box' && body.type === 'box') {
                    this.checkBoxCollision(body, collider);
                } else if ((collider.type === 'cylinder' && body.type === 'box') ||
                        (collider.type === 'box' && body.type === 'cylinder')) {
                    // For simplicity, treat cylinder as box for collision
                    // This is a simplification but works reasonably well
                    this.checkBoxCollision(body, collider);
                } else if (body.type === 'sphere') {
                    // Handle sphere collisions
                    if (collider.type === 'box') {
                        this.checkSphereBoxCollision(body, collider);
                    } else if (collider.type === 'sphere') {
                        this.checkSphereCollision(body, collider);
                    }
                } else if (collider.type === 'sphere' && body.type === 'box') {
                    this.checkSphereBoxCollision(collider, body);
                }
                // Add other collision types as needed
            }
        } catch (error) {
            console.error("Error in collision check:", error);
        }
    }
    
    checkBoxCollision(bodyA, bodyB) {
        try {
            // Validate input - make sure both bodies have the required properties
            if (!bodyA || !bodyB || !bodyA.position || !bodyB.position || 
                !bodyA.halfExtents || !bodyB.halfExtents) {
                return;
            }
            
            // Simple AABB collision detection
            const minA = bodyA.position.clone().sub(bodyA.halfExtents);
            const maxA = bodyA.position.clone().add(bodyA.halfExtents);
            const minB = bodyB.position.clone().sub(bodyB.halfExtents);
            const maxB = bodyB.position.clone().add(bodyB.halfExtents);
            
            // Check for overlap
            if (minA.x <= maxB.x && maxA.x >= minB.x &&
                minA.y <= maxB.y && maxA.y >= minB.y &&
                minA.z <= maxB.z && maxA.z >= minB.z) {
                
                // Calculate penetration depth for each axis
                const penetrationX = Math.min(maxA.x - minB.x, maxB.x - minA.x);
                const penetrationY = Math.min(maxA.y - minB.y, maxB.y - minA.y);
                const penetrationZ = Math.min(maxA.z - minB.z, maxB.z - minA.z);
                
                // Find minimum penetration axis
                let axis;
                let penetration;
                if (penetrationX < penetrationY && penetrationX < penetrationZ) {
                    axis = new THREE.Vector3(1, 0, 0);
                    penetration = penetrationX;
                } else if (penetrationY < penetrationZ) {
                    axis = new THREE.Vector3(0, 1, 0);
                    penetration = penetrationY;
                } else {
                    axis = new THREE.Vector3(0, 0, 1);
                    penetration = penetrationZ;
                }
                
                // Determine direction based on relative position
                const direction = new THREE.Vector3().subVectors(bodyA.position, bodyB.position);
                if (direction.dot(axis) < 0) {
                    axis.negate();
                }
                
                // Store original position for impact force calculation
                const originalPosition = bodyA.position.clone();
                
                // Move dynamic body out of collision with improved stability
                if (bodyA.isDynamic && !bodyB.isDynamic) {
                    // Calculate more appropriate separation distance
                    // We add a larger buffer to ensure proper separation and prevent re-collision
                    const separationDistance = penetration * 1.2;
                    
                    // Separate the objects along the collision normal
                    bodyA.position.add(axis.clone().multiplyScalar(separationDistance));
                    
                    // Calculate impact velocity along collision normal
                    const impactSpeed = bodyA.velocity.dot(axis);
                    
                    // Apply impulse response for dynamic body
                    if (impactSpeed < 0) {
                        // Calculate impulse scalar with improved physics parameters
                        const restitution = Math.min(bodyA.restitution || 0.2, bodyB.restitution || 0.2);
                        
                        // Reduce restitution at low speeds to prevent bouncing of slow objects
                        const effectiveRestitution = Math.abs(impactSpeed) < 0.5 ? 
                            restitution * (Math.abs(impactSpeed) / 0.5) : restitution;
                        
                        // Calculate impulse magnitude
                        const impulseMagnitude = -(1 + effectiveRestitution) * impactSpeed * bodyA.mass;
                        
                        // Apply impulse to velocity along collision normal
                        const impulseVector = axis.clone().multiplyScalar(impulseMagnitude);
                        bodyA.velocity.add(impulseVector.divideScalar(bodyA.mass));
                        
                        // Apply friction force perpendicular to collision normal
                        this.applyCollisionFriction(bodyA, bodyB, axis);
                        
                        // Calculate impact force for sound/effects
                        const impactForce = Math.abs(impactSpeed * bodyA.mass);
                        
                        // Only trigger collision effects if impact is significant
                        if (impactForce > 10) {
                            // Dispatch collision event that Game.js can listen for
                            if (typeof window !== 'undefined' && window.dispatchEvent) {
                                const collisionEvent = new CustomEvent('physics_collision', {
                                    detail: {
                                        bodyA,
                                        bodyB,
                                        position: bodyA.position.clone(),
                                        normal: axis.clone(),
                                        impactForce,
                                        impactSpeed: Math.abs(impactSpeed)
                                    }
                                });
                                window.dispatchEvent(collisionEvent);
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error in box collision check:", error);
        }
    }
    
    // Add a new method to apply friction during collisions
    applyCollisionFriction(bodyA, bodyB, collisionNormal) {
        // Only apply friction to dynamic bodies
        if (!bodyA.isDynamic) return;
        
        // Calculate relative velocity
        const relativeVelocity = bodyA.velocity.clone();
        
        // Calculate tangential component (perpendicular to collision normal)
        const normalComponent = collisionNormal.clone().multiplyScalar(relativeVelocity.dot(collisionNormal));
        const tangentialComponent = relativeVelocity.clone().sub(normalComponent);
        
        // Skip if tangential velocity is very small
        if (tangentialComponent.lengthSq() < 0.01) return;
        
        // Get combined friction coefficient
        const frictionCoeff = (bodyA.friction || 0.5) * (bodyB.friction || 0.5);
        
        // Apply friction force (simplified)
        const frictionDirection = tangentialComponent.clone().normalize().negate();
        const frictionMagnitude = Math.min(tangentialComponent.length() * frictionCoeff, 
                                          normalComponent.length() * frictionCoeff);
        
        // Apply friction impulse to reduce the tangential velocity component
        if (frictionMagnitude > 0) {
            const frictionImpulse = frictionDirection.multiplyScalar(frictionMagnitude * bodyA.mass);
            bodyA.velocity.add(frictionImpulse.divideScalar(bodyA.mass));
        }
    }
    
    updateDebugVisualization() {
        const lines = [];
        const colors = [];
        
        // Add wireframes for each body
        for (const body of this.bodies) {
            if (body.type === 'box') {
                this.addBoxWireframe(body, lines, colors);
            }
            // Add other shape visualizations as needed
        }
        
        // Update geometry
        if (lines.length > 0) {
            const geometry = this.debugLines.geometry;
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(lines.flat(), 3));
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors.flat(), 3));
            geometry.computeBoundingSphere();
        }
    }
    
    addBoxWireframe(body, lines, colors) {
        const position = body.position;
        const halfExtents = body.halfExtents;
        const rotation = body.quaternion;
        
        // Define box vertices
        const vertices = [
            new THREE.Vector3(-halfExtents.x, -halfExtents.y, -halfExtents.z),
            new THREE.Vector3(halfExtents.x, -halfExtents.y, -halfExtents.z),
            new THREE.Vector3(halfExtents.x, -halfExtents.y, halfExtents.z),
            new THREE.Vector3(-halfExtents.x, -halfExtents.y, halfExtents.z),
            new THREE.Vector3(-halfExtents.x, halfExtents.y, -halfExtents.z),
            new THREE.Vector3(halfExtents.x, halfExtents.y, -halfExtents.z),
            new THREE.Vector3(halfExtents.x, halfExtents.y, halfExtents.z),
            new THREE.Vector3(-halfExtents.x, halfExtents.y, halfExtents.z)
        ];
        
        // Apply rotation and position
        vertices.forEach(v => {
            v.applyQuaternion(rotation).add(position);
        });
        
        // Define edges
        const edges = [
            [0, 1], [1, 2], [2, 3], [3, 0], // bottom face
            [4, 5], [5, 6], [6, 7], [7, 4], // top face
            [0, 4], [1, 5], [2, 6], [3, 7]  // connecting edges
        ];
        
        // Add lines and colors
        for (const [i, j] of edges) {
            lines.push(
                vertices[i].x, vertices[i].y, vertices[i].z,
                vertices[j].x, vertices[j].y, vertices[j].z
            );
            
            // Green for static, blue for dynamic
            const color = body.isDynamic ? [0, 0, 1] : [0, 1, 0];
            colors.push(...color, ...color);
        }
    }
    
    // Create a rigid static body (non-moving, for ground and obstacles)
    createStaticBox(position, halfExtents, friction = 0.5) {
        const body = {
            type: 'box',
            position: position.clone(),
            quaternion: new THREE.Quaternion(),
            halfExtents: halfExtents.clone(),
            isDynamic: false,
            friction,
            restitution: 0.2,
            mesh: null
        };
        
        this.bodies.push(body);
        this.colliders.push(body);
        
        if (this.debugPhysicsLogging) {
            console.log("Created static box at", position);
        }
        
        return body;
    }
    
    // Create a rigid dynamic body (moving, for the bus and other vehicles)
    createDynamicBox(position, halfExtents, mass = 1, friction = 0.5) {
        // Create a dynamic box body
        const box = {
            type: 'box',
            position: new THREE.Vector3(position.x, position.y, position.z),
            halfExtents: new THREE.Vector3(halfExtents.x, halfExtents.y, halfExtents.z),
            quaternion: new THREE.Quaternion(),
            velocity: new THREE.Vector3(),
            angularVelocity: new THREE.Vector3(),
            forces: new THREE.Vector3(),
            torque: new THREE.Vector3(),
            mass: mass,
            friction: friction,
            restitution: 0.2,
            isDynamic: true,
            isStatic: false,
            linearDamping: 0.005,
            angularDamping: 0.5,
            maxAngularVelocity: 5.0,
            momentOfInertia: this.calculateBoxInertia(halfExtents, mass),
            threeMesh: null, // Will be set when associated with a Three.js mesh
            bus: null // Added for bus reference
        };
        
        // Add to bodies list
        this.bodies.push(box);
        
        if (this.debugPhysicsLogging) {
            console.log(`Created dynamic box at (${position.x}, ${position.y}, ${position.z}) with half-extents (${halfExtents.x}, ${halfExtents.y}, ${halfExtents.z}) and mass ${mass}`);
        }
        
        return box;
    }
    
    // Create a dynamic sphere body (for meteors and other spherical objects)
    addSphere({ radius, position, mass = 1, restitution = 0.3, friction = 0.7, collisionFilterGroup = 1, collisionFilterMask = 1 }) {
        // Create sphere body
        const sphere = {
            type: 'sphere',
            position: new THREE.Vector3(position.x, position.y, position.z),
            radius: radius,
            halfExtents: new THREE.Vector3(radius, radius, radius), // For collision detection
            quaternion: new THREE.Quaternion(),
            velocity: new THREE.Vector3(),
            angularVelocity: new THREE.Vector3(),
            forces: new THREE.Vector3(),
            torque: new THREE.Vector3(),
            mass: mass,
            friction: friction,
            restitution: restitution,
            isDynamic: true,
            isStatic: false,
            linearDamping: 0.005,
            angularDamping: 0.5,
            maxAngularVelocity: 5.0,
            collisionFilterGroup: collisionFilterGroup,
            collisionFilterMask: collisionFilterMask,
            
            // Add direct velocity setter method
            setLinearVelocity: function(vel) {
                this.velocity.set(vel.x, vel.y, vel.z);
            },
            
            // Add getter for position
            getPosition: function() {
                return this.position;
            }
        };
        
        // Add to bodies list
        this.bodies.push(sphere);
        
        // Add to colliders if it's not just a trigger
        if (collisionFilterGroup !== 0) {
            this.colliders.push(sphere);
        }
        
        if (this.debugPhysicsLogging) {
            console.log(`Created sphere at (${position.x}, ${position.y}, ${position.z}) with radius ${radius} and mass ${mass}`);
        }
        
        return sphere;
    }
    
    calculateBoxInertia(halfExtents, mass) {
        // Calculate moment of inertia for a box
        const x2 = halfExtents.x * halfExtents.x * 4;
        const y2 = halfExtents.y * halfExtents.y * 4;
        const z2 = halfExtents.z * halfExtents.z * 4;
        
        return mass * (y2 + z2 + x2) / 12;
    }
    
    // Create a static plane (for ground)
    createStaticPlane(position, normal, friction = 0.5) {
        // For simplicity, we don't really model the plane explicitly
        // Just remember the ground position and normal
        this.groundHeight = position.y;
        this.groundNormal = normal.clone().normalize();
        
        if (this.debugPhysicsLogging) {
            console.log("Created static plane at height", position.y);
        }
        
        return true;
    }
    
    // Create a vehicle (bus)
    createVehicle(chassisPosition, chassisHalfExtents) {
        // Create the chassis as a dynamic box
        const mass = 6500; // kg
        const chassisBody = this.createDynamicBox(chassisPosition, chassisHalfExtents, mass);
        
        // Add angularVelocity property if not already present
        if (!chassisBody.angularVelocity) {
            chassisBody.angularVelocity = new THREE.Vector3(0, 0, 0);
        }
        
        // Enhance with vehicle properties and methods
        const vehicle = {
            chassis: {
                ...chassisBody,
                
                // Preserve the bus reference if set
                bus: chassisBody.bus,
                
                // Methods required for Bus.js compatibility
                getGlobalPose: () => ({
                    p: { x: chassisBody.position.x, y: chassisBody.position.y, z: chassisBody.position.z },
                    q: { x: chassisBody.quaternion.x, y: chassisBody.quaternion.y, z: chassisBody.quaternion.z, w: chassisBody.quaternion.w }
                }),
                
                getLinearVelocity: () => ({ 
                    x: chassisBody.velocity.x, 
                    y: chassisBody.velocity.y, 
                    z: chassisBody.velocity.z 
                }),
                
                setLinearVelocity: (velocity) => {
                    if (!velocity) return;
                    chassisBody.velocity.set(
                        velocity.x ?? chassisBody.velocity.x,
                        velocity.y ?? chassisBody.velocity.y,
                        velocity.z ?? chassisBody.velocity.z
                    );
                },
                
                getAngularVelocity: () => ({ 
                    x: chassisBody.angularVelocity.x, 
                    y: chassisBody.angularVelocity.y, 
                    z: chassisBody.angularVelocity.z 
                }),
                
                addForce: (force) => {
                    chassisBody.forces.x += force.x;
                    chassisBody.forces.y += force.y;
                    chassisBody.forces.z += force.z;
                },
                
                addTorque: (torque) => {
                    chassisBody.torque.x += torque.x;
                    chassisBody.torque.y += torque.y;
                    chassisBody.torque.z += torque.z;
                    
                    // Update the angular velocity based on torque
                    chassisBody.angularVelocity.x += torque.x / 100;
                    chassisBody.angularVelocity.y += torque.y / 100;
                    chassisBody.angularVelocity.z += torque.z / 100;
                },
                
                setLinearDamping: (damping) => {
                    chassisBody.linearDamping = damping;
                },
                
                setAngularDamping: (damping) => {
                    chassisBody.angularDamping = damping;
                },
                
                setMaxAngularVelocity: (maxVel) => {
                    chassisBody.maxAngularVelocity = maxVel;
                },
                
                setCCD: () => {} // Not implemented in simple physics
            },
            
            wheels: []
        };
        
        if (this.debugPhysicsLogging) {
            console.log("Created vehicle at", chassisPosition);
        }
        
        return vehicle;
    }
    
    // Log physics state
    logPhysicsState() {
        console.log("Simple Physics World State:");
        console.log(`- Bodies: ${this.bodies.length}`);
        console.log(`- Gravity: (${this.gravity.x}, ${this.gravity.y}, ${this.gravity.z})`);
        
        this.bodies.forEach((body, index) => {
            console.log(`Body #${index}:`);
            console.log(`  - Type: ${body.type}`);
            console.log(`  - Dynamic: ${body.isDynamic ? 'Yes' : 'No'}`);
            console.log(`  - Position: (${body.position.x.toFixed(2)}, ${body.position.y.toFixed(2)}, ${body.position.z.toFixed(2)})`);
            if (body.isDynamic) {
                console.log(`  - Velocity: (${body.velocity.x.toFixed(2)}, ${body.velocity.y.toFixed(2)}, ${body.velocity.z.toFixed(2)})`);
                console.log(`  - Mass: ${body.mass}`);
            }
        });
    }
    
    // Toggle debug mode
    setDebugMode(enabled) {
        if (this.debugLines) {
            this.debugLines.visible = enabled;
        }
        
        this.debugPhysicsLogging = enabled;
        
        if (enabled) {
            console.log("Debug mode enabled");
            this.logPhysicsState();
        }
    }
    
    createStaticCylinder(position, radius, height, friction = 0.5) {
        const body = {
            type: 'cylinder',
            position: position.clone(),
            quaternion: new THREE.Quaternion(),
            radius,
            height,
            isDynamic: false,
            friction,
            restitution: 0.2,
            mesh: null
        };
        
        this.bodies.push(body);
        this.colliders.push(body);
        
        if (this.debugPhysicsLogging) {
            console.log("Created static cylinder at", position);
        }
        
        return body;
    }
    
    // Create a trigger zone that doesn't physically collide but can detect overlaps
    createTrigger(position, size, userData = null) {
        // Ensure size has proper width, height, depth properties
        if (!size || typeof size.width !== 'number' || typeof size.height !== 'number' || typeof size.depth !== 'number') {
            console.error("Invalid size for trigger:", size);
            // Use default size if invalid
            size = { width: 1, height: 1, depth: 1 };
        }
        
        const halfExtents = new THREE.Vector3(
            size.width / 2,
            size.height / 2,
            size.depth / 2
        );
        
        const triggerBox = {
            type: 'trigger',
            position: position.clone(),
            halfExtents: halfExtents,
            quaternion: new THREE.Quaternion(),
            isDynamic: false,
            isTrigger: true,
            userData: userData
        };
        
        // Add to a separate list for triggers (not physical colliders)
        if (!this.triggers) {
            this.triggers = [];
        }
        this.triggers.push(triggerBox);
        
        if (this.debugPhysicsLogging) {
            console.log("Created trigger at", position, "with size", size);
        }
        
        return triggerBox;
    }
    
    // Check if a body is overlapping any triggers
    checkTriggerOverlaps(body) {
        if (!this.triggers || this.triggers.length === 0) return [];
        
        const overlappingTriggers = [];
        
        for (const trigger of this.triggers) {
            if (this.boxOverlapsBox(body, trigger)) {
                overlappingTriggers.push(trigger);
            }
        }
        
        return overlappingTriggers;
    }
    
    // Utility function to check if two boxes overlap
    boxOverlapsBox(boxA, boxB) {
        try {
            // Safety check - make sure both objects have required properties
            if (!boxA || !boxB || !boxA.position || !boxB.position || 
                !boxA.halfExtents || !boxB.halfExtents) {
                return false;
            }
            
            // Verify position and halfExtents objects have x, y, z properties
            if (typeof boxA.position.x !== 'number' || typeof boxA.position.y !== 'number' || typeof boxA.position.z !== 'number' ||
                typeof boxB.position.x !== 'number' || typeof boxB.position.y !== 'number' || typeof boxB.position.z !== 'number' ||
                typeof boxA.halfExtents.x !== 'number' || typeof boxA.halfExtents.y !== 'number' || typeof boxA.halfExtents.z !== 'number' ||
                typeof boxB.halfExtents.x !== 'number' || typeof boxB.halfExtents.y !== 'number' || typeof boxB.halfExtents.z !== 'number') {
                return false;
            }
            
            // Get min/max points for both boxes
            const minA = new THREE.Vector3(
                boxA.position.x - boxA.halfExtents.x,
                boxA.position.y - boxA.halfExtents.y,
                boxA.position.z - boxA.halfExtents.z
            );
            
            const maxA = new THREE.Vector3(
                boxA.position.x + boxA.halfExtents.x,
                boxA.position.y + boxA.halfExtents.y,
                boxA.position.z + boxA.halfExtents.z
            );
            
            const minB = new THREE.Vector3(
                boxB.position.x - boxB.halfExtents.x,
                boxB.position.y - boxB.halfExtents.y,
                boxB.position.z - boxB.halfExtents.z
            );
            
            const maxB = new THREE.Vector3(
                boxB.position.x + boxB.halfExtents.x,
                boxB.position.y + boxB.halfExtents.y,
                boxB.position.z + boxB.halfExtents.z
            );
            
            // Check for overlap in all three dimensions
            return (
                minA.x <= maxB.x && maxA.x >= minB.x &&
                minA.y <= maxB.y && maxA.y >= minB.y &&
                minA.z <= maxB.z && maxA.z >= minB.z
            );
        } catch (error) {
            console.error("Error in box overlap check:", error);
            return false;
        }
    }
    
    // Remove a trigger
    removeCollider(collider) {
        // Remove from regular colliders list
        const colliderIndex = this.colliders.indexOf(collider);
        if (colliderIndex !== -1) {
            this.colliders.splice(colliderIndex, 1);
            return true;
        }
        
        // Remove from triggers list if it's a trigger
        if (this.triggers) {
            const triggerIndex = this.triggers.indexOf(collider);
            if (triggerIndex !== -1) {
                this.triggers.splice(triggerIndex, 1);
                return true;
            }
        }
        
        return false;
    }
    
    addCylinderWireframe(body) {
        if (!this.debugLines) return;
        
        const segments = 12;
        const lines = [];
        const colors = [];
        const color = new THREE.Color(0, 1, 0);
        
        // Add top and bottom circles
        for (let i = 0; i < segments; i++) {
            const theta1 = (i / segments) * Math.PI * 2;
            const theta2 = ((i + 1) / segments) * Math.PI * 2;
            
            const x1 = Math.cos(theta1) * body.radius;
            const z1 = Math.sin(theta1) * body.radius;
            
            const x2 = Math.cos(theta2) * body.radius;
            const z2 = Math.sin(theta2) * body.radius;
            
            // Top circle
            lines.push(
                new THREE.Vector3(body.position.x + x1, body.position.y + body.height / 2, body.position.z + z1),
                new THREE.Vector3(body.position.x + x2, body.position.y + body.height / 2, body.position.z + z2)
            );
            
            // Bottom circle
            lines.push(
                new THREE.Vector3(body.position.x + x1, body.position.y - body.height / 2, body.position.z + z1),
                new THREE.Vector3(body.position.x + x2, body.position.y - body.height / 2, body.position.z + z2)
            );
            
            // Connect top and bottom points
            lines.push(
                new THREE.Vector3(body.position.x + x1, body.position.y + body.height / 2, body.position.z + z1),
                new THREE.Vector3(body.position.x + x1, body.position.y - body.height / 2, body.position.z + z1)
            );
            
            // Add colors for all lines
            colors.push(color, color, color, color, color, color);
        }
        
        // Update geometry
        const lineGeometry = this.debugLines.geometry;
        const positions = lineGeometry.attributes.position;
        const lineColors = lineGeometry.attributes.color;
        
        // Find start index of new points
        const startIdx = positions.count;
        
        // Resize attributes to accommodate new lines
        const newPositions = new Float32Array((startIdx + lines.length * 2) * 3);
        const newColors = new Float32Array((startIdx + lines.length * 2) * 3);
        
        // Copy existing data
        if (startIdx > 0) {
            newPositions.set(positions.array);
            newColors.set(lineColors.array);
        }
        
        // Add new lines
        for (let i = 0; i < lines.length; i++) {
            const idx = (startIdx + i) * 3;
            const point = lines[i];
            const color = colors[Math.floor(i / 2)];
            
            newPositions[idx] = point.x;
            newPositions[idx + 1] = point.y;
            newPositions[idx + 2] = point.z;
            
            newColors[idx] = color.r;
            newColors[idx + 1] = color.g;
            newColors[idx + 2] = color.b;
        }
        
        // Update geometry attributes
        lineGeometry.setAttribute('position', new THREE.BufferAttribute(newPositions, 3));
        lineGeometry.setAttribute('color', new THREE.BufferAttribute(newColors, 3));
        
        // Set counts and flag for update
        positions.count = newPositions.length / 3;
        lineColors.count = newColors.length / 3;
        lineGeometry.setDrawRange(0, positions.count);
        lineGeometry.attributes.position.needsUpdate = true;
        lineGeometry.attributes.color.needsUpdate = true;
    }
    
    // Add method to check sphere-box collisions
    checkSphereBoxCollision(sphere, box) {
        // Get box min and max points
        const boxMin = new THREE.Vector3(
            box.position.x - box.halfExtents.x,
            box.position.y - box.halfExtents.y,
            box.position.z - box.halfExtents.z
        );
        
        const boxMax = new THREE.Vector3(
            box.position.x + box.halfExtents.x,
            box.position.y + box.halfExtents.y,
            box.position.z + box.halfExtents.z
        );
        
        // Find closest point on box to sphere center
        const closestPoint = new THREE.Vector3(
            Math.max(boxMin.x, Math.min(sphere.position.x, boxMax.x)),
            Math.max(boxMin.y, Math.min(sphere.position.y, boxMax.y)),
            Math.max(boxMin.z, Math.min(sphere.position.z, boxMax.z))
        );
        
        // Calculate distance from closest point to sphere center
        const distance = new THREE.Vector3().subVectors(closestPoint, sphere.position).length();
        
        // Check for collision - if distance is less than sphere radius
        if (distance < sphere.radius) {
            // Calculate penetration depth
            const penetration = sphere.radius - distance;
            
            // Calculate collision normal
            const normal = new THREE.Vector3();
            if (distance > 0.001) {
                normal.subVectors(closestPoint, sphere.position).normalize();
            } else {
                // If sphere center is inside box, use approximate normal
                normal.set(1, 0, 0); // Default direction
            }
            
            // Separate objects
            if (sphere.isDynamic && !box.isDynamic) {
                // Move sphere away from box
                sphere.position.sub(normal.clone().multiplyScalar(penetration * 1.2));
            } else if (!sphere.isDynamic && box.isDynamic) {
                // Move box away from sphere
                box.position.add(normal.clone().multiplyScalar(penetration * 1.2));
            } else if (sphere.isDynamic && box.isDynamic) {
                // Move both objects
                const totalMass = sphere.mass + box.mass;
                const sphereRatio = box.mass / totalMass;
                const boxRatio = sphere.mass / totalMass;
                
                sphere.position.sub(normal.clone().multiplyScalar(penetration * sphereRatio * 1.2));
                box.position.add(normal.clone().multiplyScalar(penetration * boxRatio * 1.2));
            }
            
            // Apply bounce/restitution
            this.applyCollisionResponse(sphere, box, normal);
        }
    }
    
    // Add method to check sphere-sphere collisions
    checkSphereCollision(sphereA, sphereB) {
        // Calculate distance between sphere centers
        const distance = new THREE.Vector3().subVectors(
            sphereA.position, sphereB.position
        ).length();
        
        // Check for collision - if distance is less than sum of radii
        const minDistance = sphereA.radius + sphereB.radius;
        if (distance < minDistance) {
            // Calculate penetration depth
            const penetration = minDistance - distance;
            
            // Calculate collision normal
            const normal = new THREE.Vector3();
            if (distance > 0.001) {
                normal.subVectors(sphereA.position, sphereB.position).normalize();
            } else {
                // If spheres are at same position, use default direction
                normal.set(1, 0, 0);
            }
            
            // Separate objects
            if (sphereA.isDynamic && !sphereB.isDynamic) {
                // Move sphereA away from sphereB
                sphereA.position.add(normal.clone().multiplyScalar(penetration * 1.2));
            } else if (!sphereA.isDynamic && sphereB.isDynamic) {
                // Move sphereB away from sphereA
                sphereB.position.sub(normal.clone().multiplyScalar(penetration * 1.2));
            } else if (sphereA.isDynamic && sphereB.isDynamic) {
                // Move both objects
                const totalMass = sphereA.mass + sphereB.mass;
                const sphereARatio = sphereB.mass / totalMass;
                const sphereBRatio = sphereA.mass / totalMass;
                
                sphereA.position.add(normal.clone().multiplyScalar(penetration * sphereARatio * 1.2));
                sphereB.position.sub(normal.clone().multiplyScalar(penetration * sphereBRatio * 1.2));
            }
            
            // Apply bounce/restitution
            this.applyCollisionResponse(sphereA, sphereB, normal);
        }
    }
    
    // Apply collision response (bounce/restitution)
    applyCollisionResponse(bodyA, bodyB, normal) {
        // If both bodies are static, no response needed
        if (!bodyA.isDynamic && !bodyB.isDynamic) return;
        
        // Get relative velocity
        const relVel = new THREE.Vector3();
        if (bodyA.isDynamic && bodyB.isDynamic) {
            relVel.subVectors(bodyA.velocity, bodyB.velocity);
        } else if (bodyA.isDynamic) {
            relVel.copy(bodyA.velocity);
        } else if (bodyB.isDynamic) {
            relVel.copy(bodyB.velocity).negate();
        }
        
        // Calculate impulse
        const velAlongNormal = relVel.dot(normal);
        
        // Only apply impulse if objects are moving toward each other
        if (velAlongNormal < 0) {
            // Calculate restitution (bounce)
            const restitution = Math.min(bodyA.restitution || 0.2, bodyB.restitution || 0.2);
            
            // Calculate impulse scalar
            let impulseMag = -(1 + restitution) * velAlongNormal;
            
            // Apply mass factors
            if (bodyA.isDynamic && bodyB.isDynamic) {
                impulseMag /= 1/bodyA.mass + 1/bodyB.mass;
                
                // Apply impulse to both bodies
                const impulse = normal.clone().multiplyScalar(impulseMag);
                bodyA.velocity.addScaledVector(impulse, 1/bodyA.mass);
                bodyB.velocity.addScaledVector(impulse, -1/bodyB.mass);
            } else if (bodyA.isDynamic) {
                impulseMag /= 1/bodyA.mass;
                bodyA.velocity.addScaledVector(normal, impulseMag/bodyA.mass);
            } else if (bodyB.isDynamic) {
                impulseMag /= 1/bodyB.mass;
                bodyB.velocity.addScaledVector(normal, -impulseMag/bodyB.mass);
            }
            
            // Apply friction
            this.applyCollisionFriction(bodyA, bodyB, normal);
        }
    }
    
    // Add method to remove a body from the physics world
    removeBody(body) {
        if (!body) return;
        
        // Remove from bodies array
        const bodyIndex = this.bodies.indexOf(body);
        if (bodyIndex !== -1) {
            this.bodies.splice(bodyIndex, 1);
        }
        
        // Remove from colliders array if it's there
        const colliderIndex = this.colliders.indexOf(body);
        if (colliderIndex !== -1) {
            this.colliders.splice(colliderIndex, 1);
        }
        
        // Remove from triggers array if it's there
        if (this.triggers && this.triggers.length > 0) {
            const triggerIndex = this.triggers.indexOf(body);
            if (triggerIndex !== -1) {
                this.triggers.splice(triggerIndex, 1);
            }
        }
        
        if (this.debugPhysicsLogging) {
            console.log(`Removed ${body.type} from physics world`);
        }
    }

    cleanup() {
        this.bodies = [];
        this.colliders = [];
        this.triggers = [];

        if (this.debugLines?.geometry) {
            this.debugLines.geometry.dispose();
        }
        if (this.debugLines?.material) {
            this.debugLines.material.dispose();
        }
        this.debugLines = null;
        this.debugRenderer = null;
    }
}
